import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Col,
  Row,
  Card,
  Form,
  Button,
  InputGroup,
  Spinner,
  Dropdown,
  Alert,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faSearch,
  faFilter,
  faPlus,
  faSync,
  faCheckCircle,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import Datetime from "react-datetime";
import moment from "moment-timezone";
import { Context } from "../Period";
import { includesSearchText } from "../../../../helper/text";
import AutoCreatePeriodPopup from "../sub-screen/auto-create-period-popup/AutoCreatePeriodPopup";
import { API_URL_CRAWLER } from "../../../../config";

const STATUS_OPTIONS = [
  { value: "", label: "Tất cả trạng thái" },
  { value: "0", label: "Đang bán" },
  { value: "2", label: "Đang chờ kết quả / Ngưng bán" },
  { value: "3", label: "Đã có kết quả" },
  { value: "4", label: "Đã tính thưởng" },
  { value: "5", label: "Đã tính thưởng (Loto)" },
];

const REGION_OPTIONS = [
  { value: "", label: "Tất cả khu vực" },
  { value: "MN", label: "Miền Nam" },
  { value: "MT", label: "Miền Trung" },
  { value: "MB", label: "Miền Bắc" },
];

const _FilterForm = () => {
  const { state, dispatch } = useContext(Context);

  const [from, setFrom] = useState(state.filter.from);
  const [to, setTo] = useState(state.filter.to);
  const [channelId, setChannelId] = useState(state.filter.channelId);
  const [status, setStatus] = useState(state.filter.status);
  const [region, setRegion] = useState(state.filter.region);
  const [channelSearch, setChannelSearch] = useState("");
  const [showAutoCreatePopup, setShowAutoCreatePopup] = useState(false);
  const [crawlerLoading, setCrawlerLoading] = useState(false);
  const [crawlerMessage, setCrawlerMessage] = useState(null);

  const filteredChannels = channelSearch
    ? state.data.classicUser.filter((user) =>
        includesSearchText(user.name, channelSearch),
      )
    : state.data.classicUser || [];

  // keep local state in sync with filter that may come from URL
  useEffect(() => {
    setFrom(state.filter.from);
    setTo(state.filter.to);
    setChannelId(state.filter.channelId);
    setStatus(state.filter.status);
    setRegion(state.filter.region);
  }, [
    state.filter.from,
    state.filter.to,
    state.filter.channelId,
    state.filter.status,
    state.filter.region,
  ]);

  const _handleChangeFrom = useCallback((payload) => {
    if (moment.isMoment(payload) && payload.isValid()) {
      setFrom(payload.startOf("day").toDate());
    }
  }, []);

  const _handleChangeTo = useCallback((payload) => {
    if (moment.isMoment(payload) && payload.isValid()) {
      setTo(payload.startOf("day").toDate());
    }
  }, []);

  const _submitForm = useCallback(() => {
    dispatch({
      type: "SUBMIT_FILTER",
      payload: {
        from: from,
        to: to,
        channelId: channelId,
        status: status,
        region: region,
      },
    });
  }, [dispatch, from, to, channelId, status, region]);

  const _triggerCrawler = useCallback(async () => {
    setCrawlerLoading(true);
    setCrawlerMessage(null);

    const crawlersToRun = [];
    if (region === "MT") {
      crawlersToRun.push({ type: "xsmt", name: "XSMT" });
    } else if (region === "MN") {
      crawlersToRun.push({ type: "xsmn", name: "XSMN" });
    } else {
      crawlersToRun.push(
        { type: "xsmn", name: "XSMN" },
        { type: "xsmt", name: "XSMT" }
      );
    }

    try {
      const results = await Promise.allSettled(
        crawlersToRun.map(async (crawler) => {
          const response = await fetch(`${API_URL_CRAWLER}/api/crawl/${crawler.type}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
          });
          const data = await response.json();
          return { crawler: crawler.name, success: response.ok, data };
        })
      );

      const successCount = results.filter(r => r.status === "fulfilled" && r.value.success).length;
      const failedCount = results.length - successCount;

      if (successCount > 0) {
        const message = failedCount > 0
          ? `Đã kích hoạt ${successCount}/${results.length} crawler. Đang lấy kết quả...`
          : `Đã kích hoạt ${successCount} crawler. Đang lấy kết quả...`;
        
        setCrawlerMessage({ type: "success", text: message });
        setTimeout(() => {
          _submitForm();
          setCrawlerMessage(null);
        }, 3000);
      } else {
        setCrawlerMessage({ type: "danger", text: "Lỗi khi kích hoạt crawler" });
      }
    } catch (error) {
      setCrawlerMessage({ type: "danger", text: `Lỗi: ${error.message}` });
    } finally {
      setCrawlerLoading(false);
    }
  }, [region, _submitForm]);

  return (
    <Card border="light" className="shadow-sm mb-4 border-0">
      <Card.Body className="p-4">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <h5 className="mb-0 text-primary fw-bold">
            <FontAwesomeIcon icon={faFilter} className="me-2" />
            Bộ lọc kỳ quay
          </h5>
        </div>

        <Row className="g-3">
          <Col md={4} lg={2}>
            <Form.Label className="fw-bold mb-2">Khu vực</Form.Label>
            <InputGroup>
              <InputGroup.Text className="bg-light border-end-0 text-primary">
                <FontAwesomeIcon icon={faFilter} className="fa-fw" />
              </InputGroup.Text>
              <Form.Select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="border-start-0 ps-0 bg-light fw-bold"
              >
                {REGION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </InputGroup>
          </Col>

          <Col md={4} lg={2}>
            <Form.Label className="fw-bold mb-2">Từ ngày</Form.Label>
            <Datetime
              timeFormat={false}
              value={from ? moment(from) : null}
              onChange={_handleChangeFrom}
              renderInput={(props, openCalendar) => (
                <InputGroup>
                  <InputGroup.Text className="bg-white text-muted">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </InputGroup.Text>
                  <Form.Control
                    {...props}
                    placeholder="Chọn ngày"
                  />
                </InputGroup>
              )}
            />
          </Col>

          <Col md={4} lg={2}>
            <Form.Label className="fw-bold mb-2">Đến ngày</Form.Label>
            <Datetime
              timeFormat={false}
              value={to ? moment(to) : null}
              onChange={_handleChangeTo}
              renderInput={(props, openCalendar) => (
                <InputGroup>
                  <InputGroup.Text className="bg-white text-muted">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </InputGroup.Text>
                  <Form.Control
                    {...props}
                    placeholder="Chọn ngày"
                  />
                </InputGroup>
              )}
            />
          </Col>

          <Col md={6} lg={2}>
            <Form.Label className="fw-bold mb-2">Đài (Tỉnh)</Form.Label>
            <Dropdown className="w-100">
              <Dropdown.Toggle
                variant="white"
                className="w-100 text-start d-flex align-items-center justify-content-between form-control bg-light border"
                id="dropdown-channel-select"
              >
                <span className="text-truncate">
                  {channelId
                    ? state.data.classicUser.find(
                        (ch) => String(ch.id) === String(channelId),
                      )?.name || "Tất cả đài"
                    : "Tất cả đài"}
                </span>
              </Dropdown.Toggle>

              <Dropdown.Menu
                className="w-100 p-2 shadow-sm"
                style={{ maxHeight: "300px", overflowY: "auto" }}
              >
                <div className="px-1 mb-2 sticky-top bg-white">
                  <InputGroup>
                    <InputGroup.Text className="bg-white border-end-0">
                      <FontAwesomeIcon icon={faSearch} className="text-muted" />
                    </InputGroup.Text>
                    <Form.Control
                      autoFocus
                      placeholder="Tìm đài..."
                      value={channelSearch}
                      onChange={(e) => setChannelSearch(e.target.value)}
                      className="bg-white border-start-0 ps-0"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </InputGroup>
                </div>

                <Dropdown.Item
                  active={channelId === ""}
                  onClick={() => {
                    setChannelId("");
                    setChannelSearch("");
                  }}
                  className="rounded-2"
                >
                  Tất cả đài
                </Dropdown.Item>

                {filteredChannels.length > 0 ? (
                  filteredChannels.map((user) => (
                    <Dropdown.Item
                      key={user.id}
                      active={String(channelId) === String(user.id)}
                      onClick={() => {
                        setChannelId(user.id);
                        setChannelSearch("");
                      }}
                      className="rounded-2"
                    >
                      {user.name}
                    </Dropdown.Item>
                  ))
                ) : (
                  <div className="text-center text-muted small py-2">
                    Không tìm thấy đài
                  </div>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </Col>

          <Col md={6} lg={2}>
            <Form.Label className="fw-bold mb-2">Trạng thái</Form.Label>
            <InputGroup>
              <InputGroup.Text className="bg-light border-end-0 text-primary">
                <FontAwesomeIcon icon={faFilter} className="fa-fw" />
              </InputGroup.Text>
              <Form.Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="border-start-0 ps-0 bg-light"
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </InputGroup>
          </Col>

          <Col md={12} className="mt-3 pt-3 border-top">
            {crawlerMessage && (
              <Alert
                variant={crawlerMessage.type}
                className="py-2 mb-3"
                dismissible
                onClose={() => setCrawlerMessage(null)}
              >
                <FontAwesomeIcon
                  icon={crawlerMessage.type === "success" ? faCheckCircle : faExclamationTriangle}
                  className="me-2"
                />
                {crawlerMessage.text}
              </Alert>
            )}
            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="warning"
                onClick={_triggerCrawler}
                className="fw-bold text-uppercase shadow-sm px-4"
                style={{ letterSpacing: "1px" }}
                disabled={crawlerLoading || state.isLoading}
              >
                {crawlerLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Đang lấy...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSync} className="me-2" />
                    {region === "MN" ? "Kiểm tra XSMN" : region === "MT" ? "Kiểm tra XSMT" : "Kiểm tra XSMN + XSMT"}
                  </>
                )}
              </Button>
              <Button
                variant="success"
                onClick={() => setShowAutoCreatePopup(true)}
                className="fw-bold text-uppercase shadow-sm px-4"
                style={{ letterSpacing: "1px" }}
                disabled={state.isLoading}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Tạo nhanh kỳ
              </Button>
              <Button
                variant="primary"
                onClick={_submitForm}
                className="fw-bold text-uppercase shadow-sm px-4"
                style={{ letterSpacing: "1px" }}
                disabled={state.isLoading}
              >
                {state.isLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Đang tải...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSearch} className="me-2" />
                    Tìm kiếm
                  </>
                )}
              </Button>
            </div>
          </Col>
        </Row>
      </Card.Body>

      <AutoCreatePeriodPopup
        show={showAutoCreatePopup}
        onHide={() => setShowAutoCreatePopup(false)}
        defaultRegion={region}
        onCreated={() => {
          dispatch({
            type: "SUBMIT_FILTER",
            payload: {
              from: from,
              to: to,
              channelId: channelId,
              status: status,
              region: region,
            },
          });
        }}
      />
    </Card>
  );
};

export default React.memo(_FilterForm);
